import TextM from "@common/components/text/TextM"
import { useRequestOnMount } from "@common/hooks/useRequestOnMount"
import { theme } from "@common/theme/theme"
import { apiRequest } from "@data/redux/actions/api"
import { tutorAvailabilityService } from "@data/services/tutorAvailabilityService"
import React, { useEffect, useMemo } from "react"
import { ScrollView } from "react-native"
import { connect } from "react-redux"
import { MaDispoContinuitySheet } from "../availability/MaDispoContinuitySheet"
import { MaDispoEditor } from "../availability/MaDispoEditor"
import { showEngagedSlotWarning } from "../availability/maDispoDialogs"
import { AVAILABILITY_FRESHNESS, slotKeyOf } from "../availability/maDispoSlot"

/**
 * The « Ma dispo » editor as a step of the profile stepper, in place of the two
 * legacy questions (the flat slots picker and the starting-date question).
 *
 * The step's value carries both answers — `{ slots, startingDate }` — because a
 * FormStepper step owns a single field; the stepper splits them back into the two
 * API keys on submit. `slots` holds the OPEN créneaux ONLY, exactly like the
 * standalone screen's draft: the ENGAGED ones are shown but never posted, because
 * the backend keeps them whatever the payload says
 * ({@see \App\Services\Tutor\TutorAvailability::keptSlots}). Posting them back
 * would break the save twice over — an engaged créneau outside the régime scolaire
 * grid (a cours particulier or a Live d'été routinely is) fails the grid guard and
 * 422s the WHOLE profile save, and one inside it would persist as DECLARED an hour
 * the tutor never declared, which attribution then reads.
 *
 * The whole week — grid, open/engaged split, declared créneaux and starting date
 * — is seeded from the week view, the same source the standalone screen reads.
 */
const TutorAvailabilityStepComponent = (props) => {
    const {
        field,
        control,
        setFormValues,
        weekView,
        goToNextStep,
        continuityVisible,
        onCloseContinuity,
    } = props

    useRequestOnMount(
        props.apiRequest,
        tutorAvailabilityService.getAvailability
    )

    const value = field.value ?? {}
    const openSlots = value.slots ?? []

    // Engaged créneaux are display-only, as on the standalone screen: they are not
    // the tutor's to retract from a profile form (that goes through an absence
    // request), so they stay out of the draft AND out of the payload.
    const engagedSlots = (weekView?.slots ?? []).filter((s) => s.engaged)
    const slots = [
        ...openSlots.map((slot) => ({ slot, state: "open", engaged: false })),
        ...engagedSlots,
    ]

    const change = (next) => {
        field.onChange(next)
        setFormValues({ ...control._formValues, availability: next })
    }

    // Seeded from the week view, never from the user snapshot, which no « Ma dispo »
    // save refreshes — a stale week here would be reinstated by the profile save.
    // Keyed by the server week: a remount keeps the tutor's edits, a newer week wins.
    // The key covers the OPEN half alone, the half the draft holds, so the
    // « vérifiée » comparison below stays like-for-like.
    const serverOpenSlots = useMemo(
        () =>
            (weekView?.slots ?? [])
                .filter((s) => !s.engaged)
                .map((s) => s.slot),
        [weekView]
    )
    const serverWeek = useMemo(
        () =>
            weekView
                ? JSON.stringify([
                      serverOpenSlots.map(slotKeyOf).sort(),
                      weekView.availability_starting_date ?? null,
                  ])
                : null,
        [weekView, serverOpenSlots]
    )
    useEffect(() => {
        if (!serverWeek || value.seededFrom === serverWeek) return
        change({
            seededFrom: serverWeek,
            slots: serverOpenSlots,
            startingDate:
                weekView.availability_starting_date ??
                value.startingDate ??
                null,
        })
    }, [serverWeek])

    // Green describes the SAVED week, so an edit un-verifies it on the spot — the
    // same rule as the standalone screen, which would otherwise contradict this one.
    const draftWeek = JSON.stringify([
        openSlots.map(slotKeyOf).sort(),
        value.startingDate ?? null,
    ])
    const isVerified =
        weekView?.freshness === AVAILABILITY_FRESHNESS.VERIFIED &&
        draftWeek === serverWeek

    const handleAddSlot = (day, time) => {
        const slot = `${day} ${time}`
        if (openSlots.some((s) => slotKeyOf(s) === slotKeyOf(slot))) return
        change({ ...value, slots: [...openSlots, slot] })
    }

    const handleRemoveSlot = (slot) => {
        change({
            ...value,
            slots: openSlots.filter(
                (s) => slotKeyOf(s) !== slotKeyOf(slot.slot)
            ),
        })
    }

    return (
        <ScrollView contentContainerStyle={{ paddingBottom: 16 }}>
            <TextM
                fontSize={13}
                color={theme.colors.light}
                wrap
                style={{ paddingHorizontal: 12, marginBottom: 4 }}
            >
                Indique quand et à quels créneaux tu peux prendre de nouveaux
                élèves.
            </TextM>

            <MaDispoEditor
                slots={slots}
                grid={weekView?.grid ?? {}}
                isVerified={isVerified}
                startingDate={value.startingDate}
                onPickStartingDate={(dateStr) =>
                    change({ ...value, startingDate: dateStr })
                }
                onAddSlot={handleAddSlot}
                onRemoveSlot={handleRemoveSlot}
                onPressEngaged={showEngagedSlotWarning}
            />

            {/* « Suivant » raises the engagement warning before moving on: the
                tutor reads what a recurring créneau commits them to once, here,
                and not only on the standalone screen. */}
            <MaDispoContinuitySheet
                visible={!!continuityVisible}
                onClose={onCloseContinuity}
                confirmLabel="Continuer"
                onConfirm={() => {
                    onCloseContinuity()
                    goToNextStep({ skipStepCustomAction: true })
                }}
                cancelLabel="Annuler"
            />
        </ScrollView>
    )
}

const mapStateToProps = (state) => ({
    weekView: state.data.tutorAvailability?.weekView,
})

export const TutorAvailabilityStep = connect(mapStateToProps, {
    apiRequest,
})(TutorAvailabilityStepComponent)
